import * as THREE from 'three';
import type { UnitSystem } from '@/core/Units';
import type { Heightfield } from '@/terrain/TerrainTypes';
import { formatElevation, traceContours, type ContourLine } from '@/terrain/TerrainContours';

/**
 * The topographic overlay: the site drawn as a surveyor's chart, laid back down on the ground.
 *
 * ## Why it looks hand-inked rather than technical
 *
 * The obvious palette for "contours" is a saturated CAD cyan, and it is the wrong one twice over.
 * It reads as a *tool* sitting on top of the garden rather than as a drawing of it, and it competes
 * with the one thing this overlay exists to let you judge — where the lines bunch. The warm
 * parchment inks used here sit in the same tonal family as grass, mulch and gravel, so the lines
 * read as marks on the ground; the hierarchy between thin intermediate lines and brighter index
 * lines does the work that colour would otherwise be asked to do.
 *
 * Line *weight* would be the honest way to draw that hierarchy, and it is not available: WebGL
 * ignores `linewidth` on almost every platform. Opacity and value carry it instead, which is why
 * the two line sets are two materials rather than one.
 *
 * ## Why the numbers fade instead of toggling
 *
 * An elevation figure is only worth drawing at the size it can be read at. Pinned to the world, a
 * contour label is a smear of pixels when the site is framed whole and a billboard in your face
 * when you lean into a bank — and both are worse than nothing. So the labels are faded by their
 * *apparent size on screen*, measured through the live camera, rather than by distance: it is the
 * same rule in a top-down orthographic plan as in a perspective walk-through, and it means the
 * numbers arrive exactly when they become legible. `always` and `off` remain, for when you want a
 * plan to print or a clean view to design in.
 *
 * The tracing itself is not decided here — see `@/terrain/TerrainContours`. This is a view.
 */

export type ContourLabelMode = 'auto' | 'always' | 'off';

export const CONTOUR_LABEL_MODES: readonly ContourLabelMode[] = ['auto', 'always', 'off'];

export interface ContourOverlayOptions {
  /** Which unit's round numbers the interval and the labels use. */
  unit?: UnitSystem;
  labelMode?: ContourLabelMode;
}

/**
 * How far the lines float above the ground.
 *
 * Below the reference grid's lift, deliberately: with both overlays on, the grid is the frame you
 * read coordinates off and belongs on top, while the contours are a description of the surface and
 * belong against it.
 */
const LINE_LIFT_METERS = 0.035;

/** Seconds between re-traces while the ground is being sculpted. */
const REBUILD_INTERVAL_SECONDS = 0.12;

/**
 * How tall a label's digits are, as a fraction of the site's longer side.
 *
 * Sized against the *site* rather than fixed, and the reason is what the numbers are for: a plan of
 * the whole garden is the view a contour map is read in, and a fixed world height that is legible
 * across a 46 m lot is a scrawl across a 12 m courtyard. Tying it to the span makes a label the same
 * size on screen when either site is framed whole — which is the only sense in which "the right
 * size" means anything here.
 */
const LABEL_SPAN_FRACTION = 1 / 42;
const MIN_LABEL_HEIGHT_METERS = 0.32;
const MAX_LABEL_HEIGHT_METERS = 1.8;

/** Font size the label canvas is drawn at, and the padding that keeps the halo off its edges. */
const LABEL_FONT_PX = 44;
const LABEL_PAD_PX = 10;

/** Ceiling on drawn labels. A map that costs a hundred draw calls to annotate is over-annotated. */
const MAX_LABELS = 110;

/**
 * Apparent label height, as a fraction of viewport height, at which the numbers fade in and out.
 *
 * The lower pair is legibility: about 7 px to 13 px on an 800-tall viewport, which is the band where
 * a serif italic stops being a grey smudge. The upper pair is courtesy — past roughly a tenth of the
 * viewport a number has stopped informing the view and started blocking it.
 */
const FADE_IN_MIN = 0.009;
const FADE_IN_FULL = 0.016;
const FADE_OUT_FULL = 0.12;
const FADE_OUT_MAX = 0.2;

/** How fast a label reaches its target opacity. Fast enough to feel immediate, slow enough to read as a fade. */
const FADE_RATE = 8;

/**
 * The two inks.
 *
 * Warm parchment rather than white: white lines read as a wireframe laid over the garden, and these
 * have to read as drawn on it. The index line is the brighter of the two by value, not by hue, so
 * the hierarchy survives being seen against grass, gravel, and a slope in shadow alike.
 */
const MINOR_COLOR = 0xc9b48a;
const INDEX_COLOR = 0xf2e7cd;

export class ContourOverlay {
  /** Add this to the scene's overlay root. */
  readonly object: THREE.Group;

  private field: Heightfield | null = null;
  private unit: UnitSystem;
  private mode: ContourLabelMode;
  private intervalMeters = 0;
  private indexMeters = 0;
  /** Digit height in world metres, re-derived from the site each trace. */
  private labelHeightMeters = MIN_LABEL_HEIGHT_METERS;

  private minor: THREE.LineSegments | null = null;
  private index: THREE.LineSegments | null = null;
  private readonly labels: LabelSprite[] = [];
  /** One texture per distinct elevation figure, shared by every label that carries it. */
  private readonly textures = new Map<string, LabelTexture>();
  private readonly disposables: (THREE.BufferGeometry | THREE.Material)[] = [];

  private dirty = false;
  private sinceRebuild = 0;

  private readonly anchor = new THREE.Vector3();
  private readonly ndcA = new THREE.Vector3();
  private readonly ndcB = new THREE.Vector3();
  private readonly screenUp = new THREE.Vector3();

  constructor(options: ContourOverlayOptions = {}) {
    this.object = new THREE.Group();
    this.object.name = 'topo-contours';
    // Off by default. Sculpt mode switches it on for you — see `Application.setGroundTool` — which
    // is a different thing from it being the state you always design in.
    this.object.visible = false;
    this.unit = options.unit ?? 'metric';
    this.mode = options.labelMode ?? 'auto';
  }

  get visible(): boolean {
    return this.object.visible;
  }

  get labelMode(): ContourLabelMode {
    return this.mode;
  }

  /** The vertical spacing the current map is drawn at, in metres. Zero before the first trace. */
  get contourIntervalMeters(): number {
    return this.intervalMeters;
  }

  /** The spacing between the heavier, numbered lines. Zero before the first trace. */
  get indexIntervalMeters(): number {
    return this.indexMeters;
  }

  setVisible(visible: boolean): void {
    this.object.visible = visible;
    // Ground that moved while the map was hidden is traced the moment it is shown, rather than one
    // throttle tick later — the first frame of an overlay you just asked for has to be the truth.
    if (visible && this.dirty) this.rebuild();
  }

  toggle(): boolean {
    this.setVisible(!this.object.visible);
    return this.object.visible;
  }

  /**
   * Fit the map to a terrain.
   *
   * Takes the heightfield itself rather than a copy: the sculpt tool grades that array in place, so
   * holding it is what lets {@link markDirty} mean "re-read the ground" instead of "hand me the
   * ground again". `northRadians` is the terrain mesh's own rotation — the lines are drawn in the
   * lattice's frame, so the group has to be turned with it or the map would slide off the site.
   */
  setSite(field: Heightfield, northRadians = 0): void {
    this.field = field;
    this.object.rotation.y = northRadians;
    this.dirty = true;
    this.sinceRebuild = 0;
    if (this.object.visible) this.rebuild();
  }

  /** Note that the ground has moved. The re-trace happens in {@link tick}, throttled. */
  markDirty(): void {
    this.dirty = true;
  }

  setUnit(unit: UnitSystem): void {
    if (unit === this.unit) return;
    this.unit = unit;
    // The interval is chosen in the display unit's round numbers, so this is a different map, not
    // the same one relettered.
    this.dirty = true;
    if (this.object.visible) this.rebuild();
  }

  setLabelMode(mode: ContourLabelMode): void {
    this.mode = mode;
  }

  /** Step through auto → always → off. The order a toggle button walks. */
  cycleLabelMode(): ContourLabelMode {
    const at = CONTOUR_LABEL_MODES.indexOf(this.mode);
    this.mode = CONTOUR_LABEL_MODES[(at + 1) % CONTOUR_LABEL_MODES.length] ?? 'auto';
    return this.mode;
  }

  /**
   * Per-frame work: re-trace ground that has moved, and settle the labels toward the opacity their
   * on-screen size asks for. Costs nothing while hidden.
   */
  tick(deltaSeconds: number, camera: THREE.Camera): void {
    if (!this.object.visible) return;
    if (this.dirty) {
      this.sinceRebuild += deltaSeconds;
      // Throttled rather than run per stroke-end: a drag fires ground changes every frame, and a
      // re-trace per frame would spend the sculpt tool's whole budget redrawing its own annotation.
      if (this.sinceRebuild >= REBUILD_INTERVAL_SECONDS) this.rebuild();
    }
    this.updateLabels(deltaSeconds, camera);
  }

  /* ---------------------------------- Drawing ---------------------------------- */

  private rebuild(): void {
    this.dirty = false;
    this.sinceRebuild = 0;
    this.clear();

    const field = this.field;
    if (!field) return;

    const set = traceContours(field, { unit: this.unit });
    this.intervalMeters = set.intervalMeters;
    // What the numbers are actually on. Zero when the relief is too shallow for a skeleton, which is
    // also when every line is labelled — so the legend can say "every line" rather than lie.
    this.indexMeters = set.hasIndexLines ? set.indexIntervalMeters : 0;
    if (set.lines.length === 0) return;

    const minor: number[] = [];
    const heavy: number[] = [];
    for (const line of set.lines) {
      appendLine(line.index ? heavy : minor, line);
    }
    // The gap between these two opacities is the whole hierarchy: `linewidth` is ignored by WebGL,
    // so a wide spread is the only way an index line reads as heavier than the four either side.
    this.minor = this.segments(minor, MINOR_COLOR, 0.32);
    this.index = this.segments(heavy, INDEX_COLOR, 0.95);
    this.object.add(this.minor, this.index);

    // Index contours carry the numbers, which is what makes counting the thin lines between them
    // worth doing. Flat ground rarely spans two index lines, and a map with no numbers on it is
    // just texture — so there, every line is labelled instead.
    const labelled = set.lines.filter((line) => (set.hasIndexLines ? line.index : true));
    this.buildLabels(labelled, spanOf(field));
  }

  private segments(points: number[], color: number, opacity: number): THREE.LineSegments {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(points, 3));
    const material = new THREE.LineBasicMaterial({ color, transparent: true, opacity });
    this.disposables.push(geometry, material);
    return new THREE.LineSegments(geometry, material);
  }

  /* ---------------------------------- Labels ---------------------------------- */

  /**
   * Place elevation figures along the labelled contours.
   *
   * Spacing is by distance travelled along the line, not by vertex count: a contour crossing a
   * smooth lawn has few vertices over a long run and a contour wrapping a sculpted lip has many
   * over a short one, and only arc length puts a number where the eye actually needs one. Each
   * level's run of labels is offset by a different fraction of the spacing, so numbers on adjacent
   * contours stagger instead of lining up into a false column.
   */
  private buildLabels(lines: readonly ContourLine[], siteSpanMeters: number): void {
    this.labelHeightMeters = clamp(
      siteSpanMeters * LABEL_SPAN_FRACTION,
      MIN_LABEL_HEIGHT_METERS,
      MAX_LABEL_HEIGHT_METERS,
    );
    if (typeof document === 'undefined' || lines.length === 0) return;
    const spacing = clamp(siteSpanMeters / 2.4, 8, 26);

    for (const line of lines) {
      if (this.labels.length >= MAX_LABELS) return;
      // A ring too short to carry even one number still gets one when it is a closed summit — the
      // little circle at the top of a mound is the most informative line on the map.
      const minimum = line.closed ? 2.5 : spacing * 0.55;
      if (line.lengthMeters < minimum) continue;

      const step = Math.max(spacing, line.lengthMeters / Math.max(1, Math.floor(line.lengthMeters / spacing)));
      const level = this.intervalMeters > 0 ? Math.round(line.elevationMeters / this.intervalMeters) : 0;
      const phase = ((level * 0.37) % 1) * step;
      const text = formatElevation(line.elevationMeters, this.unit, this.intervalMeters);

      let travelled = 0;
      // Half a step in, plus this level's own offset — but never past the middle, so a line only
      // long enough for one number still gets it rather than running out first.
      let nextAt = Math.min(phase + step * 0.5, line.lengthMeters * 0.5);
      const count = line.points.length / 2;
      for (let i = 1; i < count; i += 1) {
        const x0 = line.points[(i - 1) * 2] ?? 0;
        const z0 = line.points[(i - 1) * 2 + 1] ?? 0;
        const x1 = line.points[i * 2] ?? 0;
        const z1 = line.points[i * 2 + 1] ?? 0;
        const segment = Math.hypot(x1 - x0, z1 - z0);
        while (segment > 0 && travelled + segment >= nextAt) {
          const t = (nextAt - travelled) / segment;
          this.addLabel(text, x0 + (x1 - x0) * t, line.elevationMeters, z0 + (z1 - z0) * t);
          if (this.labels.length >= MAX_LABELS) return;
          nextAt += step;
        }
        travelled += segment;
      }
    }
  }

  private addLabel(text: string, x: number, elevation: number, z: number): void {
    const label = this.labelTexture(text);
    if (!label) return;
    const material = new THREE.SpriteMaterial({
      map: label.texture,
      transparent: true,
      opacity: 0,
      // Tested against the ground but never written into it: a number is an annotation on the
      // surface, so a berm in front of it should hide it, while a plant behind it should not be
      // punched through by its own transparent corners.
      depthWrite: false,
    });
    this.disposables.push(material);
    const sprite = new THREE.Sprite(material);
    // The canvas is taller than the digits — it carries the halo and the descender room — so the
    // sprite is scaled from the *glyph* height. That keeps every label the same optical size
    // whatever its text, and keeps the fade measuring what the eye actually reads.
    const height = this.labelHeightMeters * label.canvasScale;
    sprite.scale.set(height * label.aspect, height, 1);
    // Floated clear of the line rather than sat on it: at a grazing angle a sprite lying on the
    // ground is a sliver, and the number it carries is unreadable exactly when the slope is most
    // interesting.
    sprite.position.set(x, elevation + this.labelHeightMeters, z);
    sprite.renderOrder = 11;
    sprite.visible = false;
    this.labels.push({ sprite, material, opacity: 0 });
    this.object.add(sprite);
  }

  /**
   * The elevation figure, drawn once per distinct value and shared by every label carrying it.
   *
   * A light serif italic is the map convention for a *measured* number — it is what separates a
   * value read off the survey from a name written on the drawing — and it is the specific reason
   * these read as annotation rather than as UI. The dark halo is not decoration: the same figure
   * has to hold against lawn, gravel, mulch and shadowed slope, and no single fill colour does.
   *
   * The canvas is measured to the text rather than fixed: a fixed one would pad `0 m` with three
   * centimetres of transparent sprite on each side, and sprites are billboards — that padding is
   * overdraw in front of the garden on every frame it is up.
   */
  private labelTexture(text: string): LabelTexture | null {
    const cached = this.textures.get(text);
    if (cached) return cached;

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return null;
    const font = `italic ${LABEL_FONT_PX}px Georgia, "Times New Roman", serif`;
    context.font = font;
    const width = Math.ceil(context.measureText(text).width) + LABEL_PAD_PX * 2;
    const height = LABEL_FONT_PX + LABEL_PAD_PX * 2;
    canvas.width = width;
    canvas.height = height;

    // Resizing a canvas resets its context, so everything has to be set again after the measure.
    context.font = font;
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.lineWidth = 6;
    context.lineJoin = 'round';
    context.strokeStyle = 'rgba(16, 22, 14, 0.72)';
    context.strokeText(text, width / 2, height / 2);
    context.fillStyle = 'rgba(250, 248, 240, 0.94)';
    context.fillText(text, width / 2, height / 2);

    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;
    const record: LabelTexture = {
      texture,
      aspect: width / height,
      canvasScale: height / LABEL_FONT_PX,
    };
    this.textures.set(text, record);
    return record;
  }

  /**
   * Settle every label toward the opacity its size on screen asks for.
   *
   * Apparent height is measured by projecting the label's anchor and a point one glyph-height above
   * it *along the screen's own up axis*, then taking the gap in clip space. That is the one measure
   * that survives both projections: under perspective it falls off with distance as you would
   * expect, and under the orthographic top view it tracks the zoom, where a distance test would
   * report the same number however far in you had pushed.
   */
  private updateLabels(deltaSeconds: number, camera: THREE.Camera): void {
    if (this.labels.length === 0) return;
    const smoothing = Math.min(1, deltaSeconds * FADE_RATE);

    if (this.mode === 'off') {
      for (const label of this.labels) this.applyOpacity(label, 0, smoothing);
      return;
    }

    camera.updateMatrixWorld();
    camera.matrixWorldInverse.copy(camera.matrixWorld).invert();
    this.screenUp.setFromMatrixColumn(camera.matrixWorld, 1).normalize();

    for (const label of this.labels) {
      this.anchor.copy(label.sprite.position);
      this.ndcA.copy(this.anchor).project(camera);
      let target = 0;
      // Depth outside the frustum means behind the camera or beyond the far plane; either way the
      // projected gap below is meaningless rather than merely small.
      if (this.ndcA.z > -1 && this.ndcA.z < 1) {
        this.ndcB
          .copy(this.anchor)
          .addScaledVector(this.screenUp, this.labelHeightMeters)
          .project(camera);
        // NDC spans 2 units over the viewport's height, so half the gap is the fraction of it.
        const apparent = Math.abs(this.ndcB.y - this.ndcA.y) / 2;
        target =
          this.mode === 'always'
            ? 1
            : ramp(apparent, FADE_IN_MIN, FADE_IN_FULL) * (1 - ramp(apparent, FADE_OUT_FULL, FADE_OUT_MAX));
      }
      this.applyOpacity(label, target, smoothing);
    }
  }

  private applyOpacity(label: LabelSprite, target: number, smoothing: number): void {
    label.opacity += (target - label.opacity) * smoothing;
    // An exponential approach never quite arrives, so a label fading out would otherwise be drawn
    // forever at an opacity nobody can see. Below the threshold it stops being drawn at all.
    if (label.opacity < 0.02) {
      if (target === 0) label.opacity = 0;
      label.sprite.visible = false;
      return;
    }
    label.sprite.visible = true;
    label.material.opacity = label.opacity;
  }

  /* --------------------------------- Teardown --------------------------------- */

  private clear(): void {
    this.object.clear();
    this.labels.length = 0;
    this.minor = null;
    this.index = null;
    for (const item of this.disposables) item.dispose();
    this.disposables.length = 0;
  }

  dispose(): void {
    this.clear();
    for (const label of this.textures.values()) label.texture.dispose();
    this.textures.clear();
    this.field = null;
    this.object.removeFromParent();
  }
}

interface LabelSprite {
  sprite: THREE.Sprite;
  material: THREE.SpriteMaterial;
  /** Held outside the material so a label that has faded to nothing can stop being drawn at all. */
  opacity: number;
}

interface LabelTexture {
  texture: THREE.CanvasTexture;
  /** Canvas width ÷ height, so the sprite is never stretched. */
  aspect: number;
  /** Canvas height ÷ font size — how much taller the canvas is than the digits it carries. */
  canvasScale: number;
}

/** Expand one polyline into the segment pairs `THREE.LineSegments` wants, lifted off the ground. */
function appendLine(out: number[], line: ContourLine): void {
  const y = line.elevationMeters + LINE_LIFT_METERS;
  const count = line.points.length / 2;
  for (let i = 1; i < count; i += 1) {
    out.push(
      line.points[(i - 1) * 2] ?? 0,
      y,
      line.points[(i - 1) * 2 + 1] ?? 0,
      line.points[i * 2] ?? 0,
      y,
      line.points[i * 2 + 1] ?? 0,
    );
  }
}

function spanOf(field: Heightfield): number {
  return Math.max(field.widthMeters, field.lengthMeters);
}

/** Smooth 0→1 ramp between two thresholds, flat outside them. */
function ramp(value: number, from: number, to: number): number {
  if (to <= from) return value >= to ? 1 : 0;
  const t = clamp((value - from) / (to - from), 0, 1);
  return t * t * (3 - 2 * t);
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
